import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import type { components } from '../../api/schema'
import { apiErrorCode, apiErrorDetail } from '../../lib/apiError'
import { useFormatters } from '../../lib/format'
import { AssetPicker } from '../AssetPicker'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'
import { BookingDialog, useRefreshLetting } from '../viewings/BookingDialog'
import { dayKey, ViewingBadges } from '../viewings/ViewingsAgendaPage'
import type { RentalApplication } from './shared'

type Offer = components['schemas']['RentalOffer']
type AcceptResult = components['schemas']['RentalOfferAcceptResult']

/** The application's viewings and the booking entry point (spec 2026-10-25-12). */
export function ViewingsCard({ org, app, editable }: { org: string; app: RentalApplication; editable: boolean }) {
    const { t } = useTranslation('viewings')
    const { formatDate } = useFormatters()
    const [booking, setBooking] = useState(false)
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/viewings', {
        params: { path: { orgCode: org }, query: { application_uid: app.uid } },
    })
    const viewings = data?.viewings ?? []
    return (
        <Card data-testid="rental-application-viewings">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">{t('card.title')}</CardTitle>
                {editable && app.listing && (
                    <Button size="sm" variant="outline" onClick={() => setBooking(true)} data-testid="rental-application-book-viewing">{t('card.book')}</Button>
                )}
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {viewings.length === 0 && <p className="text-muted-foreground">{t('card.none')}</p>}
                {viewings.map((v) => (
                    <div key={v.uid} className="flex flex-wrap items-center gap-2" data-testid="rental-application-viewing-row" data-status={v.status}>
                        <span>{formatDate(v.starts_at, { dateStyle: 'medium', timeStyle: 'short' })}</span>
                        <Badge variant="secondary">{t(`status.${v.status}`)}</Badge>
                        <ViewingBadges viewing={v} />
                        <Link to="/$org/rental-applications/viewings" params={{ org }} search={{ view: 'day', date: dayKey(new Date(v.starts_at)), viewing: v.uid }} className="text-xs underline">{t('card.open_agenda')}</Link>
                    </div>
                ))}
            </CardContent>
            {app.listing && (
                <BookingDialog org={org} open={booking} onOpenChange={setBooking} listingUid={app.listing.uid} assetUid={app.listing.asset_uid} applicationUids={[app.uid]} />
            )}
        </Card>
    )
}

type OfferForm = { rent: string; currency: string; frequency: string; moveIn: string; term: string; occupants: string; expires: string }

function formOf(o?: Offer, app?: RentalApplication): OfferForm {
    return {
        rent: o ? String(o.rent_minor / 100) : app?.listing?.asking_rent_minor != null ? String(app.listing.asking_rent_minor / 100) : '',
        currency: o?.currency ?? app?.listing?.currency ?? 'EUR',
        frequency: o?.rent_frequency ?? 'monthly',
        moveIn: o?.move_in ?? app?.desired_move_in ?? '',
        term: o?.term_months != null ? String(o.term_months) : '',
        occupants: o?.occupants != null ? String(o.occupants) : app?.group_size != null ? String(app.group_size) : '',
        expires: o?.expires_at ? o.expires_at.slice(0, 10) : '',
    }
}

/**
 * The offer dialog: a first offer or a counter-offer, then the acceptance
 * with the tenant grouping, the annexes, the gate's override, and the result.
 */
function OfferDialog({ org, app, mode, offer, onClose }: { org: string; app: RentalApplication; mode: 'make' | 'counter' | 'accept' | null; offer?: Offer; onClose: () => void }) {
    const { t } = useTranslation('viewings')
    const { formatMoney } = useFormatters()
    const refresh = useRefreshLetting()
    const [form, setForm] = useState<OfferForm>(formOf())
    const [madeBy, setMadeBy] = useState('applicant')
    const [groupKind, setGroupKind] = useState('colocation')
    const [annexes, setAnnexes] = useState<{ uid: string; name: string }[]>([])
    const [unlettable, setUnlettable] = useState(false)
    const [confirm, setConfirm] = useState(false)
    const [reason, setReason] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<AcceptResult | null>(null)
    // One Idempotency-Key per opening: a double submit accepts once.
    const [idemKey, setIdemKey] = useState('')
    useEffect(() => {
        if (mode) {
            setIdemKey(crypto.randomUUID())
            setForm(formOf(mode === 'make' ? undefined : offer, app))
            setMadeBy(mode === 'counter' && offer?.made_by === 'applicant' ? 'landlord' : 'applicant')
            setError(null)
            setResult(null)
            setUnlettable(false)
            setConfirm(false)
            setReason('')
            setAnnexes([])
        }
        // Reset when the dialog opens only: the refetch after an acceptance
        // must not wipe the result it shows.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode])
    const fail = (err: unknown) => {
        const code = apiErrorCode(err)
        if (code === 'OBJECT_NOT_LETTABLE') setUnlettable(true)
        setError(code ? t(`errors.${code}`, { defaultValue: apiErrorDetail(err, t('errors.generic')) }) : apiErrorDetail(err, t('errors.generic')))
    }
    const done = () => {
        refresh()
        onClose()
    }
    const make = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/offers', { onSuccess: done, onError: fail })
    const counter = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-offers/{offerUid}/counter', { onSuccess: done, onError: fail })
    const accept = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-offers/{offerUid}/accept', {
        onSuccess: (r) => {
            refresh()
            setError(null)
            setResult(r as AcceptResult)
        },
        onError: fail,
    })
    const propose = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/recirculate', {
        onSuccess: () => refresh(),
        onError: fail,
    })
    const body = () => ({
        made_by: madeBy as 'applicant',
        rent_minor: form.rent ? Math.round(Number(form.rent) * 100) : undefined,
        currency: form.currency,
        rent_frequency: form.frequency,
        move_in: form.moveIn,
        term_months: form.term ? Number(form.term) : undefined,
        occupants: form.occupants ? Number(form.occupants) : undefined,
        expires_at: form.expires ? new Date(form.expires + 'T23:59:59').toISOString() : undefined,
    })
    const tenants = app.members.filter((m) => m.role !== 'guarantor').length
    const field = (key: keyof OfferForm, testId: string, type = 'text') => (
        <div>
            <Label className="mb-1 block text-sm">{t(`offer.${key}`)}</Label>
            <Input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} disabled={mode === 'accept'} data-testid={testId} />
        </div>
    )

    return (
        <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="rental-offer-dialog" data-mode={mode ?? ''} className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t(`offer.title_${mode ?? 'make'}`)}</DialogTitle>
                    <DialogDescription>{t(`offer.description_${mode ?? 'make'}`)}</DialogDescription>
                </DialogHeader>
                {result ? (
                    <div className="space-y-3 text-sm" data-testid="rental-offer-result">
                        <p>{t('accept.done')}</p>
                        <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: result.lease_contract_uid }} className="underline" data-testid="rental-offer-lease-link">
                            {t('accept.open_lease')}
                        </Link>
                        {result.recirculation.length > 0 && (
                            <div className="space-y-2">
                                <p className="font-medium">{t('recirculation.title')}</p>
                                {result.recirculation.map((r) => (
                                    <div key={r.application_uid} className="rounded-md border p-2" data-testid="rental-offer-recirculation-row">
                                        <p>{r.lead_name ?? '—'}</p>
                                        {r.listings.length === 0 && <p className="text-xs text-muted-foreground">{t('recirculation.none')}</p>}
                                        {r.listings.map((l) => (
                                            <div key={l.listing_uid} className="mt-1 flex items-center justify-between gap-2">
                                                <span>{l.asset_name} · {t(`recirculation.verdict.${l.verdict}`)}</span>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={propose.isPending}
                                                    onClick={() => propose.mutate({ params: { path: { orgCode: org, applicationUid: r.application_uid } }, body: { listing_uid: l.listing_uid } })}
                                                    data-testid="rental-offer-recirculation-propose"
                                                >
                                                    {t('recirculation.propose')}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                            {field('rent', 'rental-offer-rent', 'number')}
                            {field('currency', 'rental-offer-currency')}
                            <div>
                                <Label className="mb-1 block text-sm">{t('offer.frequency')}</Label>
                                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })} disabled={mode === 'accept'}>
                                    <SelectTrigger data-testid="rental-offer-frequency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {['monthly', 'quarterly', 'weekly', 'yearly'].map((f) => <SelectItem key={f} value={f}>{t(`frequency.${f}`)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            {field('moveIn', 'rental-offer-move-in', 'date')}
                            {field('term', 'rental-offer-term', 'number')}
                            {field('occupants', 'rental-offer-occupants', 'number')}
                            {field('expires', 'rental-offer-expires', 'date')}
                            {mode !== 'accept' && (
                                <div>
                                    <Label className="mb-1 block text-sm">{t('offer.made_by')}</Label>
                                    <Select value={madeBy} onValueChange={setMadeBy}>
                                        <SelectTrigger data-testid="rental-offer-made-by"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="applicant">{t('offer.by.applicant')}</SelectItem>
                                            <SelectItem value="landlord">{t('offer.by.landlord')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                        {mode === 'accept' && offer && (
                            <div className="space-y-3 border-t pt-3">
                                <p className="text-sm">{t('accept.summary', { rent: formatMoney(offer.rent_minor, offer.currency), move_in: offer.move_in })}</p>
                                {tenants > 1 && (
                                    <div>
                                        <Label className="mb-1 block text-sm">{t('accept.group_kind')}</Label>
                                        <Select value={groupKind} onValueChange={setGroupKind}>
                                            <SelectTrigger data-testid="rental-offer-group-kind-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="colocation" data-testid="rental-offer-group-kind-colocation">{t('accept.kind.colocation')}</SelectItem>
                                                <SelectItem value="couple" data-testid="rental-offer-group-kind-couple">{t('accept.kind.couple')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div data-testid="rental-offer-annex-picker">
                                    <Label className="mb-1 block text-sm">{t('accept.annexes')}</Label>
                                    <AssetPicker
                                        org={org}
                                        testId="rental-offer-annex-search"
                                        placeholder={t('accept.annex_placeholder')}
                                        onChange={(uid, name) => uid && !annexes.some((a) => a.uid === uid) && setAnnexes([...annexes, { uid, name }])}
                                    />
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {annexes.map((a) => (
                                            <Badge key={a.uid} variant="outline" data-testid="rental-offer-annex">
                                                {a.name}
                                                <button type="button" className="ml-1" onClick={() => setAnnexes(annexes.filter((x) => x.uid !== a.uid))} aria-label={t('accept.remove_annex')}>×</button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                {unlettable && (
                                    <div className="space-y-2 rounded-md border border-warning p-3">
                                        <label className="flex items-center gap-2 text-sm">
                                            <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(v === true)} data-testid="rental-offer-confirm-unlettable" />
                                            {t('accept.confirm_unlettable')}
                                        </label>
                                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('accept.reason_placeholder')} data-testid="rental-offer-unlettable-reason" />
                                    </div>
                                )}
                            </div>
                        )}
                        {error && <p className="text-sm text-destructive" data-testid="rental-offer-error">{error}</p>}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="rental-offer-dialog-close">{result ? t('close') : t('cancel')}</Button>
                    {!result && mode === 'make' && (
                        <Button disabled={!form.moveIn || make.isPending} onClick={() => make.mutate({ params: { path: { orgCode: org, applicationUid: app.uid } }, body: body() })} data-testid="rental-offer-submit">
                            {t('offer.submit')}
                        </Button>
                    )}
                    {!result && mode === 'counter' && offer && (
                        <Button disabled={counter.isPending} onClick={() => counter.mutate({ params: { path: { orgCode: org, offerUid: offer.uid } }, body: body() })} data-testid="rental-offer-submit">
                            {t('offer.counter')}
                        </Button>
                    )}
                    {!result && mode === 'accept' && offer && (
                        <Button
                            disabled={accept.isPending || (unlettable && (!confirm || reason.trim().length < 10))}
                            onClick={() =>
                                accept.mutate({
                                    params: { path: { orgCode: org, offerUid: offer.uid }, header: { 'Idempotency-Key': idemKey } },
                                    body: {
                                        group_kind: tenants > 1 ? (groupKind as 'colocation') : undefined,
                                        annex_asset_uids: annexes.map((a) => a.uid),
                                        confirm_unlettable: unlettable && confirm,
                                        reason: unlettable ? reason : undefined,
                                    },
                                })
                            }
                            data-testid="rental-offer-accept-confirm"
                        >
                            {t('accept.confirm')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** The offers of an application, versions in order. */
export function OffersCard({ org, app, editable }: { org: string; app: RentalApplication; editable: boolean }) {
    const { t } = useTranslation('viewings')
    const { formatMoney } = useFormatters()
    const refresh = useRefreshLetting()
    const [mode, setMode] = useState<'make' | 'counter' | 'accept' | null>(null)
    const [current, setCurrent] = useState<Offer | undefined>()
    const [error, setError] = useState<string | null>(null)
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/offers', {
        params: { path: { orgCode: org, applicationUid: app.uid } },
    })
    const withdraw = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-offers/{offerUid}/withdraw', {
        onSuccess: () => refresh(),
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    const offers = data?.offers ?? []
    const hasOpen = offers.some((o) => o.status === 'open')
    const accepted = app.stage === 'offer_accepted' || app.stage === 'leased'
    const open = (m: 'make' | 'counter' | 'accept', o?: Offer) => {
        setCurrent(o)
        setMode(m)
    }
    return (
        <Card data-testid="rental-application-offers">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">{t('offers.title')}</CardTitle>
                {editable && app.listing && !hasOpen && !accepted && (
                    <Button size="sm" variant="outline" onClick={() => open('make')} data-testid="rental-application-make-offer">{t('offers.make')}</Button>
                )}
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {offers.length === 0 && <p className="text-muted-foreground">{t('offers.none')}</p>}
                {offers.map((o) => (
                    <div key={o.uid} className="flex flex-wrap items-center gap-2 border-b pb-2" data-testid="rental-application-offer-row" data-status={o.status} data-version={o.version}>
                        <span className="font-medium">v{o.version}</span>
                        <span>{formatMoney(o.rent_minor, o.currency)} · {t(`frequency.${o.rent_frequency}`, { defaultValue: o.rent_frequency })}</span>
                        <span className="text-muted-foreground">{t('offers.move_in', { date: o.move_in })}</span>
                        <span className="text-muted-foreground">{t(`offer.by.${o.made_by}`)}</span>
                        <Badge variant={o.status === 'accepted' ? 'success' : o.status === 'open' ? 'secondary' : 'outline'} data-testid="rental-application-offer-status">
                            {t(`offer_status.${o.status}`)}
                        </Badge>
                        {editable && o.status === 'open' && (
                            <span className="ml-auto flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => open('counter', o)} data-testid="rental-offer-counter-button">{t('offers.counter')}</Button>
                                <Button size="sm" variant="ghost" onClick={() => withdraw.mutate({ params: { path: { orgCode: org, offerUid: o.uid } } })} data-testid="rental-offer-withdraw-button">
                                    {t('offers.withdraw')}
                                </Button>
                                <Button size="sm" onClick={() => open('accept', o)} data-testid="rental-offer-accept-button">{t('offers.accept')}</Button>
                            </span>
                        )}
                        {canWithdrawAccepted(o, app) && editable && (
                            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => withdraw.mutate({ params: { path: { orgCode: org, offerUid: o.uid } } })} data-testid="rental-offer-withdraw-button">
                                {t('offers.withdraw')}
                            </Button>
                        )}
                    </div>
                ))}
                {app.lease_contract_uid && (
                    <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: app.lease_contract_uid }} className="underline" data-testid="rental-application-lease-link">
                        {t('accept.open_lease')}
                    </Link>
                )}
                {error && <p className="text-destructive" data-testid="rental-application-offers-error">{error}</p>}
            </CardContent>
            <OfferDialog org={org} app={app} mode={mode} offer={current} onClose={() => setMode(null)} />
        </Card>
    )
}

function canWithdrawAccepted(o: Offer, app: RentalApplication) {
    return o.status === 'accepted' && app.stage === 'offer_accepted'
}
